import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import toast from "react-hot-toast";

const SUBJECTS = [
  "General Enquiry",
  "Order Issue",
  "Product Feedback",
  "Bulk / Catering Order",
  "Partnership / Collaboration",
  "Complaint",
  "Other",
];

const DEFAULT_INFO = {
  address: "10/11, 3rd Main, 3rd Cross, Kempapura, Bengaluru 560024",
  phone: "+91 98765 43210",
  email: "info@proteinspot.in",
  hours: "Every day, 8 am – 9 pm",
  mapLabel: "Hebbal, Bengaluru",
  mapUrl: "",
  mapEmbed: "",
};

export default function ContactPage() {
  const navigate = useNavigate();

  /* ── contact info from backend ── */
  const [info, setInfo] = useState(DEFAULT_INFO);
  useEffect(() => {
    api
      .get("/contact/info")
      .then((res) => setInfo(res.data))
      .catch(() => {}); // keep defaults on error
  }, []);

  /* helper: build INFO cards from info state */
  const infoCards = [
    {
      icon: "📍",
      title: "Our Kitchen",
      line1: info.address?.split(",")[0]?.trim() || "",
      line2: info.address?.split(",").slice(1).join(",").trim() || "",
    },
    {
      icon: "📞",
      title: "Call Us",
      line1: info.phone,
      line2: "Mon–Sat, 8 am – 8 pm",
      href: info.phone ? `tel:${info.phone.replace(/\s/g, "")}` : undefined,
    },
    {
      icon: "✉️",
      title: "Email Us",
      line1: info.email,
      line2: "We reply within 24 hours",
      href: info.email ? `mailto:${info.email}` : undefined,
    },
    {
      icon: "⏰",
      title: "Delivery Hours",
      line1: info.hours,
      line2: "Fresh prep, zero preservatives",
    },
  ];

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: SUBJECTS[0],
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim() && !form.phone.trim())
      e.contact = "Provide email or phone";
    if (form.email.trim() && !/\S+@\S+\.\S+/.test(form.email))
      e.email = "Invalid email";
    if (!form.message.trim()) e.message = "Please write your message";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll get back to you soon 🌿");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to send. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  /* shared input style factory */
  const inp = (field) => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 14px",
    borderRadius: 12,
    border: `1.5px solid ${errors[field] ? "#fca5a5" : "rgba(45,139,78,0.2)"}`,
    background: errors[field] ? "#fff5f5" : "white",
    fontSize: 14,
    color: "#111827",
    outline: "none",
    fontFamily: "'Roboto',sans-serif",
    transition: "border-color 0.15s, box-shadow 0.15s",
  });
  const focus = (e) => {
    e.target.style.borderColor = "#2D8B4E";
    e.target.style.boxShadow = "0 0 0 3px rgba(45,139,78,0.1)";
  };
  const blur = (field, e) => {
    e.target.style.boxShadow = "none";
    e.target.style.borderColor = errors[field]
      ? "#fca5a5"
      : "rgba(45,139,78,0.2)";
  };

  const LBL = ({ children }) => (
    <label
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 700,
        color: "#374151",
        marginBottom: 5,
        textTransform: "uppercase",
        letterSpacing: ".4px",
      }}
    >
      {children}
    </label>
  );
  const ERR = ({ field }) =>
    errors[field] ? (
      <p style={{ fontSize: 11, color: "#ef4444", margin: "3px 0 0" }}>
        {errors[field]}
      </p>
    ) : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Roboto',sans-serif",
        background: "#f8fafb",
      }}
    >
      <Navbar />

      {/* ── Hero banner ── */}
      <div
        style={{
          background:
            "linear-gradient(135deg,#0a3d1f 0%,#0f5230 50%,#1a7040 100%)",
          padding: "24px 20px 20px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "rgba(76,175,114,0.08)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,179,71,0.06)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto",
            textAlign: "center",
            position: "relative",
          }}
        >
          <div style={{ fontSize: 30, marginBottom: 8 }}>💬</div>
          <h1
            style={{
              color: "#fff",
              fontWeight: 900,
              fontSize: "clamp(20px,4vw,28px)",
              margin: "0 0 8px",
              letterSpacing: "-0.8px",
              lineHeight: 1.1,
            }}
          >
            Get in Touch
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.62)",
              fontSize: 13,
              margin: "0 auto",
              lineHeight: 1.6,
              maxWidth: 460,
            }}
          >
            Questions, feedback, or a bulk catering order? We'd love to hear
            from you. Our team replies within a few hours.
          </p>
        </div>
      </div>

      {/* ── Content ── */}
      <div
        style={{
          flex: 1,
          maxWidth: 1080,
          margin: "0 auto",
          width: "100%",
          padding: "28px 20px 48px",
          boxSizing: "border-box",
        }}
      >
        <div className="cg-grid">
          {/* ── Left: info ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ marginBottom: 4 }}>
              <h2
                style={{
                  fontSize: 19,
                  fontWeight: 900,
                  color: "#14532d",
                  margin: "0 0 5px",
                  letterSpacing: "-0.3px",
                }}
              >
                ProteinSpot
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: "#6b7280",
                  margin: 0,
                  lineHeight: 1.65,
                }}
              >
                Cold- juices, fresh salads, sandwiches and smoothies —
                made fresh every day with zero preservatives.
              </p>
            </div>

            {infoCards.map((item) => (
              <div
                key={item.title}
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: "15px 18px",
                  border: "1px solid rgba(45,139,78,0.1)",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                  display: "flex",
                  gap: 14,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: "rgba(45,139,78,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 17,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                <div>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#2D8B4E",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      margin: "0 0 3px",
                    }}
                  >
                    {item.title}
                  </p>
                  {item.href ? (
                    <a
                      href={item.href}
                      style={{
                        display: "block",
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "#111827",
                        textDecoration: "none",
                        margin: 0,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#2D8B4E")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#111827")
                      }
                    >
                      {item.line1}
                    </a>
                  ) : (
                    <p
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "#111827",
                        margin: 0,
                      }}
                    >
                      {item.line1}
                    </p>
                  )}
                  <p
                    style={{
                      fontSize: 12,
                      color: "#9ca3af",
                      margin: "2px 0 0",
                    }}
                  >
                    {item.line2}
                  </p>
                </div>
              </div>
            ))}

            {/* Map embed — uses the mapEmbed src set from admin */}
            <div
              style={{
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(45,139,78,0.12)",
                height: 200,
                position: "relative",
              }}
            >
              {info.mapEmbed ? (
                <>
                  <iframe
                    title="Our Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0, display: "block" }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={info.mapEmbed}
                  />
                  {info.mapUrl && (
                    <a
                      href={info.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#fff",
                        background: "rgba(45,139,78,0.88)",
                        padding: "4px 10px",
                        borderRadius: 20,
                        textDecoration: "none",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      Open in Maps →
                    </a>
                  )}
                </>
              ) : (
                <div
                  style={{
                    height: "100%",
                    background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                >
                  <div style={{ fontSize: 28 }}>🗺️</div>
                  <p
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#374151",
                      margin: 0,
                    }}
                  >
                    {info.mapLabel || "Our Location"}
                  </p>
                  {info.mapUrl && (
                    <a
                      href={info.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 11,
                        color: "#2D8B4E",
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                    >
                      Open in Maps →
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Right: form ── */}
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              padding: "30px 28px",
              border: "1px solid rgba(45,139,78,0.1)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            }}
          >
            {sent ? (
              <div style={{ textAlign: "center", padding: "40px 16px" }}>
                <div style={{ fontSize: 56, marginBottom: 14 }}>✅</div>
                <h3
                  style={{
                    fontSize: 21,
                    fontWeight: 900,
                    color: "#14532d",
                    margin: "0 0 10px",
                  }}
                >
                  Message Received!
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: "#6b7280",
                    lineHeight: 1.7,
                    margin: "0 0 26px",
                  }}
                >
                  Thanks for reaching out. We'll get back to you within 24
                  hours.
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => {
                      setSent(false);
                      setForm({
                        name: "",
                        email: "",
                        phone: "",
                        subject: SUBJECTS[0],
                        message: "",
                      });
                      setErrors({});
                    }}
                    style={{
                      padding: "10px 22px",
                      borderRadius: 12,
                      border: "none",
                      cursor: "pointer",
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: "#fff",
                      background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                    }}
                  >
                    Send Another
                  </button>
                  <button
                    onClick={() => navigate("/")}
                    style={{
                      padding: "10px 22px",
                      borderRadius: 12,
                      cursor: "pointer",
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: "#374151",
                      background: "#fff",
                      border: "1.5px solid #e5e7eb",
                    }}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginBottom: 22 }}>
                  <h2
                    style={{
                      fontSize: 19,
                      fontWeight: 900,
                      color: "#14532d",
                      margin: "0 0 3px",
                      letterSpacing: "-0.3px",
                    }}
                  >
                    Send us a message
                  </h2>
                  <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
                    Fields marked * are required
                  </p>
                </div>

                {/* Name + Phone */}
                <div
                  className="cg-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  <div>
                    <LBL>Name *</LBL>
                    <input
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your name"
                      style={inp("name")}
                      onFocus={focus}
                      onBlur={(e) => blur("name", e)}
                    />
                    <ERR field="name" />
                  </div>
                  <div>
                    <LBL>Phone</LBL>
                    <input
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+91 XXXXX XXXXX"
                      type="tel"
                      style={inp("contact")}
                      onFocus={focus}
                      onBlur={(e) => blur("contact", e)}
                    />
                  </div>
                </div>

                {/* Email */}
                <div style={{ marginBottom: 14 }}>
                  <LBL>Email *</LBL>
                  <input
                    value={form.email}
                    onChange={set("email")}
                    placeholder="your@email.com"
                    type="email"
                    style={inp("email")}
                    onFocus={focus}
                    onBlur={(e) => blur("email", e)}
                  />
                  <ERR field="email" />
                  <ERR field="contact" />
                </div>

                {/* Subject */}
                <div style={{ marginBottom: 14 }}>
                  <LBL>Subject</LBL>
                  <select
                    value={form.subject}
                    onChange={set("subject")}
                    onFocus={focus}
                    onBlur={(e) => blur("subject", e)}
                    style={{
                      ...inp("subject"),
                      cursor: "pointer",
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 12px center",
                      paddingRight: 34,
                      appearance: "none",
                    }}
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div style={{ marginBottom: 22 }}>
                  <LBL>Message *</LBL>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    rows={5}
                    placeholder="Tell us how we can help you…"
                    style={{
                      ...inp("message"),
                      resize: "vertical",
                      minHeight: 110,
                      lineHeight: 1.65,
                    }}
                    onFocus={focus}
                    onBlur={(e) => blur("message", e)}
                  />
                  <ERR field="message" />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  style={{
                    width: "100%",
                    padding: "13px",
                    borderRadius: 12,
                    border: "none",
                    cursor: sending ? "not-allowed" : "pointer",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#fff",
                    background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                    boxShadow: "0 4px 16px rgba(45,139,78,0.28)",
                    opacity: sending ? 0.7 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!sending) {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 22px rgba(45,139,78,0.38)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow =
                      "0 4px 16px rgba(45,139,78,0.28)";
                  }}
                >
                  {sending ? (
                    <>
                      <span
                        style={{
                          width: 15,
                          height: 15,
                          border: "2px solid rgba(255,255,255,0.4)",
                          borderTopColor: "#fff",
                          borderRadius: "50%",
                          animation: "spin 0.65s linear infinite",
                          display: "inline-block",
                        }}
                      />{" "}
                      Sending…
                    </>
                  ) : (
                    "📩 Send Message"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />

      <style>{`
        .cg-grid { display:grid; grid-template-columns:1fr 1.65fr; gap:32px; align-items:start; }
        @media(max-width:768px) { .cg-grid { grid-template-columns:1fr !important; } }
        @media(max-width:500px) { .cg-row  { grid-template-columns:1fr !important; } }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
